import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Loader2, ShieldCheck, ArrowRight, LogOut } from "lucide-react";

export default function Activate() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);

  if (user && user.activated) return <Navigate to="/dashboard" replace />;

  const startCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background afha-grain flex flex-col" data-testid="activate-page">
      <header className="bg-white border-b border-border">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <Logo />
          <button onClick={handleLogout} data-testid="activate-logout" className="text-sm text-muted-foreground hover:text-patriot flex items-center gap-1.5">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-patriot font-bold">Welcome, {user?.name?.split(" ")[0]}</p>
            <h1 className="font-display text-3xl sm:text-4xl text-navy mt-2 tracking-tight">Activate your AFHA account</h1>
            <p className="mt-3 text-muted-foreground">A one-time <span className="font-semibold text-navy">$50</span> activation fee unlocks your full back office.</p>
          </div>

          <div className="bg-white border-2 border-navy rounded-xl p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-12 w-12 rounded-lg bg-navy flex items-center justify-center"><ShieldCheck className="h-6 w-6 text-white" /></div>
              <div>
                <h2 className="font-display text-2xl text-navy">Full Representative Access</h2>
                <p className="text-sm text-muted-foreground">Everything you need to grow with AFHA.</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm text-navy">
              {[
                "$1,000 commission on every personal sale",
                "Onboard physicians & track them",
                "Your own recruiting link",
                "6-level override income ($500 / sale across your team)",
                "Team dashboard, earnings & compensation calculator",
                "Training library",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5"><Check className="h-5 w-5 text-patriot shrink-0" /> {f}</li>
              ))}
            </ul>
            <div className="mt-6 pt-6 border-t border-border">
              <p className="font-display text-4xl font-black text-navy">$50<span className="text-lg font-normal text-muted-foreground"> one-time</span></p>
              <Button onClick={startCheckout} disabled={loading} data-testid="pay-activation-fee" className="mt-4 w-full h-12 rounded-full bg-patriot hover:bg-patriot-dark text-white gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Pay $50 &amp; Activate <ArrowRight className="h-4 w-4" /></>}
              </Button>
              <p className="text-xs text-muted-foreground mt-3 text-center">Secure checkout via Stripe. Test card: 4242 4242 4242 4242.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
